import React, { FC } from "react";
import {
	Box,
	Button,
	DialogContent,
	FormControlLabel,
	Grid,
	makeStyles,
	Radio,
	RadioGroup,
	Select,
	MenuItem,
	FormControl,
} from "@material-ui/core";
import { verifyError } from "Components/Common/helpers/verifyError";
import {
	useTranslateWorkingDaysKey,
	WORKING_DAYS_KEYS,
} from "Components/CompanySetting/CompanySettingHelper";
import { getWorkingDays } from "Components/CompanySetting/helpers/getWorkingDays";
import { getWorkingDaysKey } from "Components/CompanySetting/helpers/getWorkingDaysKey";
import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import { WorkingDaysKey } from "Components/CompanySetting/types/WorkingDaysKey";
import ModalTitle from "Components/Modal/ModalTitle";
import { px } from "Configs/theme";
import TimePickerField from "Fields/TimePickerField";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment from "moment";
import { useTranslation } from "Providers/TranslationProvider";
import { Controller, useForm } from "react-hook-form";
import ImageView from "Views/ImageView";
import * as yup from "yup";
import { TIME_FORMAT } from "./constants/TIME_FORMAT";
import { getCompanyWorkingHours } from "./helpers/getCompanyWorkingHours";
import useLocalStorageState from "use-local-storage-state";
import { CalendarType } from "Components/Navigation/NavigationHelper";
import { useAuthUser } from "Components/Auth/hooks/useAuthUser";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import { getIsDayViewEnabled } from "Components/ProfileLimit/helpers/getIsDayViewEnabled";

interface Props {
	close: () => void;
	isCloseDisabled: boolean;
}

interface FormValues {
	startTime: moment.Moment;
	endTime: moment.Moment;
	workingDaysKey: WorkingDaysKey;
}

const WorkingHoursForm: FC<Props> = (props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const companySetting = useCompanySetting((state) => state.item);
	const workingHours = getCompanyWorkingHours(companySetting);
	const userId = useAuthUser((state) => state.user?.uid);
	const profileLimits = useProfileLimits((state) => state.items);
	const isDayViewEnabled = userId && getIsDayViewEnabled(userId, profileLimits);
	const [defaultCalendarView, setDefaultCalendarView] = useLocalStorageState<CalendarType>(
		"planning-board-default-calendar-view",
		CalendarType.WEEK
	);

	// Reset default view to WEEK if user doesn't have day view access but has DAY set as default
	React.useEffect(() => {
		if (!isDayViewEnabled && defaultCalendarView === CalendarType.DAY) {
			setDefaultCalendarView(CalendarType.WEEK);
		}
	}, [isDayViewEnabled, defaultCalendarView, setDefaultCalendarView]);
	const snackbar = useSnackbar();
	const translateWorkingDaysKey = useTranslateWorkingDaysKey();

	function getAvailableCalendarTypes(): CalendarType[] {
		const baseTypes = [CalendarType.WEEK, CalendarType.MONTH];
		
		if (isDayViewEnabled) {
			return [CalendarType.DAY, ...baseTypes];
		}
		
		return baseTypes;
	}

	const defaultValues: FormValues = {
		startTime: moment(workingHours.start, TIME_FORMAT),
		endTime: moment(workingHours.end, TIME_FORMAT),
		workingDaysKey: getWorkingDaysKey(companySetting?.workingDays),
	};

	const validationSchema = yup.object().shape({
		endTime: yup
			.object<moment.Moment>()
			.required()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("WorkingHoursForm_Label_EndTime"),
				})
			)
			.test(
				"test-endTimeAfterStartTime",
				t("Common_Form_Error_EndTimeBeforeStartTime"),
				function (endTime: moment.Moment) {
					if (this.parent.startTime?.isValid() && endTime.isValid()) {
						return endTime.isAfter(this.parent.startTime);
					} else {
						return false;
					}
				}
			),
	});

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;
	const values = form.getValues();
	const isDisabled = form.formState.isSubmitting;

	async function onSubmit(formValues: FormValues) {
		const start = formValues.startTime.format(TIME_FORMAT);
		const end = formValues.endTime.format(TIME_FORMAT);
		const workingDays = getWorkingDays(formValues.workingDaysKey);

		try {
			await useCompanySetting.getState().updateItem({
				workingHours: {
					start,
					end,
				},
				workingDays,
			});

			props.close();
		} catch (error) {
			snackbar.showMessage(verifyError(error).message);
		}
	}

	return (
		<form onSubmit={form.handleSubmit(onSubmit)}>
			<ModalTitle
				close={props.isCloseDisabled ? undefined : props.close}
				title={t("WorkingHoursForm_Title")}
			>
				<Button
					disabled={isDisabled}
					onClick={form.handleSubmit(onSubmit)}
					color="primary"
					variant="contained"
				>
					{t("Common_Dialog_Save")}
				</Button>
			</ModalTitle>
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={5} className={classes.gridColumn}>
						<ImageView
							style={{ width: "100%" }}
							alt={t("WorkingHoursForm_Title")}
							src="working-hours.png"
						/>
					</Grid>
					<Grid item xs={12} sm={7} className={classes.gridColumn}>
						<Box className={classes.paragraphBox}>
							{t("WorkingHoursForm_SubTitle_WorkingHours")}
						</Box>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<TimePickerField
									name="startTime"
									label={t("WorkingHoursForm_Label_StartTime")}
									control={control}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TimePickerField
									name="endTime"
									label={t("WorkingHoursForm_Label_EndTime")}
									control={control}
								/>
							</Grid>
						</Grid>
						<Box className={classes.paragraphBox}>
							{t("WorkingHoursForm_SubTitle_WorkingDays")}
						</Box>
						<Controller
							as={RadioGroup}
							control={control}
							name="workingDaysKey"
							value={values.workingDaysKey}
							className={classes.radioGroup}
							row
						>
							{WORKING_DAYS_KEYS.map((workingDaysKey) => (
								<FormControlLabel
									key={workingDaysKey}
									value={workingDaysKey}
									disabled={isDisabled}
									control={<Radio color="primary" />}
									labelPlacement="end"
									label={translateWorkingDaysKey(workingDaysKey)}
								/>
							))}
						</Controller>
						{isDayViewEnabled && (
							<>
								<Box className={classes.paragraphBox}>
									{t("WorkingHoursForm_SubTitle_DefaultView")}
								</Box>
								<FormControl fullWidth>
									<Select
										labelId="default-calendar-view-label"
										value={defaultCalendarView}
										onChange={(e) => setDefaultCalendarView(e.target.value as CalendarType)}
										disabled={isDisabled}
									>
										{getAvailableCalendarTypes().map((calendarType) => (
											<MenuItem key={calendarType} value={calendarType}>
												{calendarType === CalendarType.DAY && t("CalendarNavigationHelper_CalendarType_Day")}
												{calendarType === CalendarType.WEEK && t("CalendarNavigationHelper_CalendarType_Week")}
												{calendarType === CalendarType.MONTH && t("CalendarNavigationHelper_CalendarType_Month")}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</>
						)}
					</Grid>
				</Grid>
			</DialogContent>
		</form>
	);
};

export default WorkingHoursForm;

const useStyles = makeStyles({
	gridColumn: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
	},
	paragraphBox: {
		fontSize: "16px",
		fontWeight: "bold",
		paddingTop: px.xxl,
		paddingBottom: px.m,
	},
	radioGroup: {
		flexDirection: "column",
		paddingBottom: px.l,
	},
});
